import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';

import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { DEMANDS_FILTER_MIN_DATE } from '../../../../../Catalog/Consts/demands-filter-panel.const';
import { DemandsDateRangeChange } from '../../../../../Catalog/Interfaces/demands-filter-panel.interface';
import { formatDateOnly, parseDateOnly } from '../../../../../Catalog/Shared/date-only.util';
import { DATE_RANGE_PRESETS_PANEL_CLASS } from '../../../Consts/date-range-presets.const';
import { DateRangePresetOption } from '../../../Interfaces/date-range-preset-option.interface';
import { buildDateRangePresetOptions } from '../../../Shared/date-range-preset.util';
import { DateRangePresetId } from '../../../Types/date-range-preset-id.type';

@Component({
  selector: 'app-all-demands-date-range',
  standalone: true,
  imports: [COMMON_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './all-demands-date-range.component.html',
  styleUrl: './all-demands-date-range.component.scss',
})
export class AllDemandsDateRangeComponent {
  readonly label = input.required<string>();
  readonly from = input<string | null>(null);
  readonly to = input<string | null>(null);
  readonly showPresets = input<boolean>(false);

  readonly rangeChange = output<DemandsDateRangeChange>();

  readonly fromControl = new FormControl<Date | null>(null);
  readonly toControl = new FormControl<Date | null>(null);

  readonly minDate = DEMANDS_FILTER_MIN_DATE;
  readonly maxDate = new Date();

  readonly presetsPanelClass = DATE_RANGE_PRESETS_PANEL_CLASS;
  readonly presetOptions = buildDateRangePresetOptions({ min: this.minDate, max: this.maxDate });

  readonly inverted = signal(false);

  readonly activePresetId = computed<DateRangePresetId | null>(() => {
    const from = this.from();
    const to = this.to();
    const active = this.presetOptions.find(
      (option) => formatDateOnly(option.from) === from && formatDateOnly(option.to) === to,
    );
    return active?.id ?? null;
  });

  constructor() {
    this._syncControlsFromInputs();
  }

  private _syncControlsFromInputs(): void {
    effect(() => {
      const from = parseDateOnly(this.from());
      const to = parseDateOnly(this.to());
      if (this.fromControl.value?.getTime() !== from?.getTime()) {
        this.fromControl.setValue(from, { emitEvent: false });
      }
      if (this.toControl.value?.getTime() !== to?.getTime()) {
        this.toControl.setValue(to, { emitEvent: false });
      }
      this.inverted.set(false);
    });
  }

  onDateInputChange(pickerOpen: boolean): void {
    if (!pickerOpen) this.commitRange();
  }

  commitRange(): void {
    const from = formatDateOnly(this.fromControl.value);
    const to = formatDateOnly(this.toControl.value);
    const inverted = from !== null && to !== null && from > to;
    this.inverted.set(inverted);
    if (inverted || (from === this.from() && to === this.to())) return;
    this.rangeChange.emit({ from, to });
  }

  applyPreset(option: DateRangePresetOption, picker: MatDateRangePicker<Date>): void {
    this.fromControl.setValue(option.from);
    this.toControl.setValue(option.to);
    picker.close();
  }
}
